import type { FetchProductQuery } from '@eliadmin/core/src/api/products/product-api';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { fetchProductSegmentsProductsBySegmentType } from '@eliadmin/core/src/api/product-segments/product-segments-api';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';

const DEFAULT_PRODUCTS_LIMIT = 12;

function deduplicateProducts<T extends Pick<Product, 'id'>>(products: T[]) {
  const seen = new Set<number>();

  return products.filter(product => {
    if (seen.has(product.id)) return false;

    seen.add(product.id);
    return true;
  });
}

export async function fetchSegmentFirstProducts({
  locale,
  segmentSlug,
  query,
  authToken,
  limit = DEFAULT_PRODUCTS_LIMIT,
}: {
  locale: string;
  segmentSlug: string;
  query: FetchProductQuery;
  authToken?: string;
  limit?: number;
}) {
  const [segmentProducts, defaultProducts] = await Promise.all([
    fetchProductSegmentsProductsBySegmentType({
      locale,
      segmentType: segmentSlug,
      query: {
        page: 1,
        results: limit,
      },
      authToken,
    })
      .then(response => response.data)
      .catch(() => []),
    fetchProducts({
      locale,
      query: {
        page: 1,
        results: limit,
        ...query,
      },
      authToken,
    })
      .then(response => response.data)
      .catch(() => []),
  ]);

  const segmentProductsUnique = deduplicateProducts(segmentProducts).slice(0, limit);
  const segmentProductIds = new Set(segmentProductsUnique.map(product => product.id));
  const remainingCount = Math.max(limit - segmentProductsUnique.length, 0);
  const defaultProductsUnique = deduplicateProducts(defaultProducts).filter(
    product => !segmentProductIds.has(product.id),
  );

  return [...segmentProductsUnique, ...defaultProductsUnique.slice(0, remainingCount)];
}
